import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Typography, Box, CircularProgress, Popover } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import CheckIcon from '@material-ui/icons/Check'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import ConfirmDeleteIcon from 'styles/images/icons/confirmDelete.svg'
import { makeStyles } from '@material-ui/core/styles'
import { Toast } from 'components/shared/Toaster'
import ConfirmationModal from '../../Drawer/ConfirmationModal'
import Loader from 'components/shared/Loader'
import { Icon } from 'styles'
import strings from 'strings'
import AuthService from 'tools/AuthService'
import { useAppContext } from 'context/AppContext'
import {
  CARD_BORDER,
  CRITICAL,
  FAINT,
  INK,
  INSET,
  MUTED,
  NEUTRAL_TINT,
  PAGE,
  PURPLE,
  buildAgreementDetailRows,
  buildAgreementSummarySentence,
  buildHeadline,
  buildLienDetailRows,
  buildLienSummarySentence,
  buildTileNotes,
  buildWhyThisMatters,
  buildYearSubtitle,
  collectPeriods,
  countActiveAgreements,
  countActiveLiens,
  findPeriodForYear,
  formatCount,
  formatCurrency,
  formatPeriodShort,
  formatPulledAt,
  formatTin,
  getStatusLabel,
  getStatusMeta,
  isPending,
  primaryFormName
} from './taxesAdapters'
import { deleteTaxrockRecord, getTaxrockRecords, lookupTaxrock, saveTaxrockRecord, updateBusiness } from '../../../../../actions/applicants'

const LOOKUP_COST_TYPE = 'TAXPAYER_EIN_LOOKUP'

// Mirrors TaxRockDemoFixture on the backend; any other EIN comes back Not monitored in demo mode.
const DEMO_EINS = [
  { ein: '116142109', label: 'Compliant' },
  { ein: '220784103', label: 'At risk' },
  { ein: '334602857', label: 'Not compliant' },
  { ein: '663572335', label: 'Active resolution' },
  { ein: '440565102', label: 'Pending' }
]

const cleanEin = (value) => String(value || '').replace(/\D/g, '').slice(0, 9)
const formatEin = (digits) => (digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits)

// A saved record keeps the whole TaxRock payload in snapshotJson as a string;
// everything the tab draws lives inside it.
const withSnapshot = (record) => {
  try {
    return { ...record, snapshot: JSON.parse(record.snapshotJson) }
  } catch (err) {
    return { ...record, snapshot: null }
  }
}

const normalizeRecords = (res) => {
  const list = Array.isArray(res) ? res : (res ? [res] : [])
  return list.map(withSnapshot)
}

const lookupCostOf = (creditCosts) => {
  if (!Array.isArray(creditCosts)) return 0
  const match = creditCosts.find((cost) => (cost.requestType || cost.type) === LOOKUP_COST_TYPE)
  return match ? match.creditCost : 0
}

// Only the parts the payload actually names: the type, the TIN, and the form
// when some period in the report reports one.
const headerMeta = (result, periods, record) => {
  const tin = formatTin(result) || (record?.businessEin
    ? formatTin({ type: 'Business', tin: record.businessEin })
    : null)
  const formName = primaryFormName(periods)
  return [
    result?.type,
    tin ? `${strings.TAXES_TIN} ${tin}` : null,
    formName ? `${strings.TAXES_FORM} ${formName}` : null
  ].filter(Boolean).join(' · ')
}

const useStyles = makeStyles((theme) => ({
  page: {
    backgroundColor: PAGE,
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: '400px'
  },
  centeredState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    textAlign: 'center'
  },

  // ---- page header, sitting on the page ground rather than in a card
  pageHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    padding: `0 ${theme.spacing(1)}px`
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: INK,
    letterSpacing: '-0.01em'
  },
  subline: {
    fontSize: 13,
    color: FAINT,
    marginTop: 4
  },
  headActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  ghostButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#fff',
    color: INK,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 10,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  historyRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: `0 ${theme.spacing(1)}px`
  },
  historyLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: FAINT,
    marginRight: 4
  },
  reportHeadActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  discardButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#fff',
    color: CRITICAL,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 10,
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': { borderColor: CRITICAL }
  },
  historyTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    maxWidth: '100%',
    minWidth: 0,
    backgroundColor: '#fff',
    color: INK,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 10,
    padding: '8px 10px 8px 14px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer'
  },
  historyTriggerText: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 8,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  historyEin: {
    fontWeight: 500,
    color: FAINT
  },
  historyCount: {
    fontSize: 12,
    fontWeight: 600,
    color: MUTED,
    backgroundColor: NEUTRAL_TINT,
    borderRadius: 999,
    padding: '2px 8px',
    whiteSpace: 'nowrap'
  },
  historyPanel: {
    marginTop: 6,
    minWidth: 420,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 360,
    padding: 6,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 12,
    boxShadow: '0 12px 32px rgba(16,26,22,0.12)'
  },
  historyOption: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    textAlign: 'left',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 8,
    padding: '10px 12px 10px 8px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: PAGE }
  },
  historyCheck: {
    width: 16,
    flexShrink: 0,
    display: 'inline-flex'
  },
  historyOptionBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0
  },
  historyOptionName: {
    fontSize: 14,
    fontWeight: 600,
    color: INK,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  historyOptionMeta: {
    fontSize: 12,
    color: FAINT
  },
  startCard: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: '#fff',
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 14,
    boxShadow: '0 1px 2px rgba(16,26,22,0.03)',
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    textAlign: 'left'
  },
  startLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: MUTED
  },
  startInput: {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    color: INK,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 16,
    letterSpacing: '0.04em',
    outline: 'none',
    '&:focus': { borderColor: INK }
  },
  startHelp: {
    fontSize: 13,
    color: FAINT
  },
  demoRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    fontSize: 12,
    color: FAINT
  },
  demoChip: {
    backgroundColor: NEUTRAL_TINT,
    color: INK,
    border: 'none',
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  startButton: {
    width: '100%',
    justifyContent: 'center',
    marginTop: theme.spacing(1)
  },
  einForm: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1)
  },
  einRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  einInput: {
    width: 170,
    backgroundColor: '#fff',
    color: INK,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 14,
    letterSpacing: '0.04em',
    outline: 'none',
    '&:focus': { borderColor: INK }
  },
  einError: {
    fontSize: 12,
    color: CRITICAL
  },
  einHint: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    fontSize: 13,
    color: MUTED
  },
  einHintLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: PURPLE,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  solidButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: INK,
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '11px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '4px 10px',
    borderRadius: 999,
    whiteSpace: 'nowrap'
  },
  pillDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: 'currentColor'
  },

  // ---- cards
  card: {
    backgroundColor: '#fff',
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 14,
    boxShadow: '0 1px 2px rgba(16,26,22,0.03)'
  },
  cardPad: {
    padding: theme.spacing(3)
  },
  cardHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: `${theme.spacing(2.5)}px ${theme.spacing(3)}px`,
    borderBottom: `1px solid ${CARD_BORDER}`
  },
  cardTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: INK
  },
  cardMeta: {
    fontSize: 13,
    color: FAINT
  },

  // ---- report card
  reportHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    padding: `${theme.spacing(2.5)}px ${theme.spacing(3)}px`,
    borderBottom: `1px solid ${CARD_BORDER}`
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: FAINT
  },
  name: {
    fontSize: 21,
    fontWeight: 700,
    color: INK,
    marginTop: 6
  },
  verdict: {
    borderRadius: 10,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3)
  },
  verdictHeadline: {
    fontSize: 15,
    fontWeight: 700,
    color: INK,
    lineHeight: 1.5
  },
  verdictWhy: {
    fontSize: 14,
    color: MUTED,
    lineHeight: 1.6,
    marginTop: 2
  },
  verdictWhyLabel: {
    fontWeight: 700,
    color: INK
  },
  tilesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3)
  },
  tile: {
    flex: '1 1 180px',
    minWidth: 150
  },
  tileLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: MUTED
  },
  tileValue: {
    fontSize: 32,
    fontWeight: 700,
    color: INK,
    lineHeight: 1.15,
    margin: '6px 0 4px',
    fontVariantNumeric: 'tabular-nums'
  },
  tileNote: {
    fontSize: 13,
    color: FAINT
  },

  // ---- balance by year
  yearRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
    borderBottom: `1px solid ${CARD_BORDER}`,
    cursor: 'pointer'
  },
  yearRowLast: {
    borderBottom: 'none'
  },
  yearLeft: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(3),
    minWidth: 0,
    flexWrap: 'wrap'
  },
  yearLabel: {
    fontSize: 17,
    fontWeight: 700,
    color: INK,
    minWidth: 52
  },
  yearSubtitle: {
    fontSize: 14,
    color: MUTED
  },
  yearRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0
  },
  dueLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: FAINT
  },
  dueValue: {
    fontSize: 21,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums'
  },
  toggle: {
    width: 30,
    height: 30,
    borderRadius: 9,
    border: `1px solid ${CARD_BORDER}`,
    backgroundColor: '#fff',
    color: MUTED,
    fontSize: 15,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0
  },
  detailStrip: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    backgroundColor: INSET,
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
    borderBottom: `1px solid ${CARD_BORDER}`
  },
  detailCell: {
    flex: '1 1 130px',
    minWidth: 110
  },
  detailLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: FAINT
  },
  detailValue: {
    fontSize: 15,
    fontWeight: 600,
    color: INK,
    marginTop: 2,
    fontVariantNumeric: 'tabular-nums'
  },
  emptyYears: {
    padding: `${theme.spacing(5)}px ${theme.spacing(3)}px`,
    textAlign: 'center'
  },
  emptyYearsTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: INK
  },
  emptyYearsBody: {
    fontSize: 14,
    color: MUTED,
    lineHeight: 1.6,
    maxWidth: 520,
    margin: '8px auto 0'
  },

  // ---- lien / agreement cards
  cardsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'flex-start'
  },
  halfCard: {
    flex: '1 1 340px',
    minWidth: 300
  },
  countPill: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    padding: '4px 10px',
    borderRadius: 999,
    whiteSpace: 'nowrap'
  },
  cardBody: {
    padding: theme.spacing(3)
  },
  bodyText: {
    fontSize: 14,
    lineHeight: 1.6,
    color: MUTED
  },
  detailLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    marginTop: theme.spacing(2.5),
    fontSize: 14,
    fontWeight: 600,
    color: INK,
    cursor: 'pointer'
  },
  detailTable: {
    marginTop: theme.spacing(2)
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: `${theme.spacing(1.5)}px 0`,
    borderTop: `1px solid ${CARD_BORDER}`,
    fontSize: 14
  },
  detailRowLabel: {
    color: MUTED
  },
  detailRowValue: {
    fontWeight: 700,
    color: INK,
    textAlign: 'right'
  }
}))

const StatusPill = ({ classes, status, style }) => {
  const meta = getStatusMeta(status)
  return (
    <span
      className={classes.statusPill}
      style={{ backgroundColor: meta.tint, color: meta.accent, ...style }}
    >
      <span className={classes.pillDot} />
      {getStatusLabel(status)}
    </span>
  )
}

const Taxes = ({ applicationId, businessEin, creditCosts, onBusinessUpdated }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [records, setRecords] = useState([])
  const [openYear, setOpenYear] = useState(null)
  const [pulling, setPulling] = useState(false)
  // The EIN to search, prefilled from the business and editable like the
  // Healthcare Search fields; the business EIN itself only changes on request.
  const savedEin = cleanEin(businessEin)
  const [ein, setEin] = useState(savedEin)
  const [savingEin, setSavingEin] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [historyAnchor, setHistoryAnchor] = useState(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const [discarding, setDiscarding] = useState(false)

  useEffect(() => {
    setEin(savedEin)
  }, [savedEin])
  const { isReadOnlyUser, creditUsageEnabled } = AuthService.getClientFeatures()
  const { isDemoMode, demoModeFeatureActive } = useAppContext()

  const refresh = useCallback(async (selectEin) => {
    const res = await getTaxrockRecords(applicationId)().catch(() => null)
    const list = normalizeRecords(res)
    setRecords(list)
    setOpenYear(null)
    // After a pull, show the report for the EIN just pulled; otherwise the latest.
    const pulled = selectEin ? list.find((record) => cleanEin(record.businessEin) === selectEin) : null
    const shown = pulled || list[0]
    setSelectedId(shown ? shown.recordId : null)
    // The input tracks the report on screen so Re-pull refreshes what you're looking at.
    if (shown) setEin(cleanEin(shown.businessEin))
  }, [applicationId])

  useEffect(() => {
    if (!applicationId) {
      setLoading(false)
      return
    }
    refresh().finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  const einValid = ein.length === 9

  const handlePull = async (e) => {
    if (e && e.preventDefault) e.preventDefault()
    if (!applicationId || !einValid || pulling) return
    setPulling(true)
    try {
      const interaction = await lookupTaxrock(applicationId, ein, !!isDemoMode)()
      const response = interaction?.response
      if (!response) {
        Toast({ type: 'error', message: strings.TAXES_PULL_EMPTY })
        return
      }
      await saveTaxrockRecord(applicationId, { businessEin: ein, response })()
      await refresh(ein)
      Toast({ type: 'success', message: strings.TAXES_PULL_SUCCESS })
    } catch (err) {
      Toast({
        type: 'error',
        message: err?.status === 409 ? strings.TAXES_RECONNECT_REQUIRED : strings.TAXES_PULL_FAILED
      })
    } finally {
      setPulling(false)
    }
  }

  const activeRecord = records.find((record) => record.recordId === selectedId) || records[0] || null
  const snapshot = activeRecord?.snapshot || null
  const result = snapshot?.result || null
  const periods = useMemo(() => (result ? collectPeriods(result) : []), [result])
  const years = result?.balanceByYear || []
  const lookupCost = lookupCostOf(creditCosts)

  const creditBadge = creditUsageEnabled && lookupCost
    ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6 }}>
        {lookupCost}
        <Icon folder="icons" fileName="credits.svg" style={{ display: 'flex' }} />
      </span>
    )
    : null

  // Only an EIN TaxRock actually matched is worth writing back to the business;
  // a NotMonitored or unreadable record has no result and proves nothing.
  const matchedEin = result ? cleanEin(activeRecord?.businessEin) : ''


  const handleSaveEin = async () => {
    if (matchedEin.length !== 9 || savingEin) return
    setSavingEin(true)
    try {
      await updateBusiness(applicationId, {
        identifications: [{ identification: matchedEin, identificationType: 'EIN', primary: true }]
      })()
      Toast({ type: 'success', message: strings.TAXES_EIN_SAVED })
      if (onBusinessUpdated) onBusinessUpdated()
    } catch (err) {
      Toast({ type: 'error', message: strings.TAXES_EIN_SAVE_FAILED })
    } finally {
      setSavingEin(false)
    }
  }

  const pullDisabled = pulling || isReadOnlyUser || !einValid
  const showEinError = ein.length > 0 && !einValid
  // Recommend saving the matched EIN when the business has none, or a different one.
  const showSavePrompt = matchedEin.length === 9 && matchedEin !== savedEin && !isReadOnlyUser

  const demoChips = isDemoMode && demoModeFeatureActive && !isReadOnlyUser
    ? (
      <div className={classes.demoRow}>
        <span>{strings.TAXES_DEMO_EINS}</span>
        {DEMO_EINS.map((demo) => (
          <button
            key={demo.ein}
            type="button"
            className={classes.demoChip}
            onClick={() => setEin(demo.ein)}
            title={formatEin(demo.ein)}
            disabled={pulling}
          >
            {demo.label}
          </button>
        ))}
      </div>
    )
    : null

  const handleDiscard = async () => {
    if (!activeRecord?.recordId || discarding) return
    setDiscarding(true)
    try {
      await deleteTaxrockRecord(applicationId, activeRecord.recordId)()
      setConfirmingDiscard(false)
      await refresh()
      Toast({ type: 'success', message: strings.TAXES_DISCARDED })
    } catch (err) {
      Toast({ type: 'error', message: strings.TAXES_DISCARD_FAILED })
    } finally {
      setDiscarding(false)
    }
  }

  const selectRecord = (record) => {
    setHistoryAnchor(null)
    setConfirmingDiscard(false)
    setSelectedId(record.recordId)
    setEin(cleanEin(record.businessEin))
    setOpenYear(null)
  }

  // Every EIN pulled on this application keeps its own saved report. The picker
  // is a popover list so it stays one line however many EINs get tried.
  const historyRow = records.length > 1 && (
    <div className={classes.historyRow}>
      <span className={classes.historyLabel}>{strings.TAXES_SAVED_REPORTS}</span>
      <button
        type="button"
        className={classes.historyTrigger}
        onClick={(event) => setHistoryAnchor(event.currentTarget)}
        aria-haspopup="listbox"
        aria-expanded={!!historyAnchor}
        disabled={pulling}
      >
        <span className={classes.historyTriggerText}>
          {activeRecord?.taxpayerName || strings.TAXES_STATUS_NOT_MONITORED}
          <span className={classes.historyEin}>{formatEin(cleanEin(activeRecord?.businessEin))}</span>
        </span>
        <span className={classes.historyCount}>{`${records.length} ${strings.TAXES_SAVED_SUFFIX}`}</span>
        <ExpandMoreIcon style={{ fontSize: 18, color: MUTED }} />
      </button>
      <Popover
        open={!!historyAnchor}
        anchorEl={historyAnchor}
        onClose={() => setHistoryAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.historyPanel }}
      >
        <div role="listbox" aria-label={strings.TAXES_SAVED_REPORTS}>
          {records.map((record) => {
            const active = record.recordId === activeRecord?.recordId
            const recordStatus = record.snapshot?.currentStatus?.status || record.status
            const pulled = formatPulledAt(record.updatedAt || record.createdAt)
            return (
              <button
                key={record.recordId}
                type="button"
                role="option"
                aria-selected={active}
                className={classes.historyOption}
                style={active ? { backgroundColor: PAGE } : undefined}
                onClick={() => selectRecord(record)}
              >
                <span className={classes.historyCheck}>
                  {active && <CheckIcon style={{ fontSize: 16, color: INK }} />}
                </span>
                <span className={classes.historyOptionBody}>
                  <span className={classes.historyOptionName}>
                    {record.taxpayerName || strings.TAXES_STATUS_NOT_MONITORED}
                  </span>
                  <span className={classes.historyOptionMeta}>
                    {[`${strings.TAXES_EIN} ${formatEin(cleanEin(record.businessEin))}`,
                      pulled ? `${strings.TAXES_PULLED_PREFIX} ${pulled}` : null].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <StatusPill classes={classes} status={recordStatus} />
              </button>
            )
          })}
        </div>
      </Popover>
    </div>
  )

  // Buttons are type="button": ApplicationOfferDetail.scss hides every
  // button[type="submit"] under .applicant .appDetailTab. Enter still submits
  // the single-input forms.
  const einForm = (className, label) => (
    <form className={classes.einForm} onSubmit={handlePull}>
      <div className={classes.einRow}>
        <input
          className={classes.einInput}
          value={formatEin(ein)}
          onChange={(event) => setEin(cleanEin(event.target.value))}
          placeholder={strings.TAXES_EIN_PLACEHOLDER}
          aria-label={strings.TAXES_EIN}
          inputMode="numeric"
          disabled={pulling || isReadOnlyUser}
        />
        <button
          type="button"
          onClick={handlePull}
          className={className}
          disabled={pullDisabled}
          style={pullDisabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
        >
          {pulling && <CircularProgress size={12} thickness={5} style={{ color: 'inherit' }} />}
          {pulling ? strings.TAXES_PULLING : label}
          {!pulling && creditBadge}
        </button>
      </div>
      {showEinError && <Typography className={classes.einError}>{strings.TAXES_EIN_INVALID}</Typography>}
      {demoChips}
      {showSavePrompt && (
        <div className={classes.einHint}>
          <span>
            {`${strings.TAXES_EIN_MATCHED_PREFIX} ${formatEin(matchedEin)}. `}
            {savedEin
              ? `${strings.TAXES_EIN_REPLACE_PROMPT} (${formatEin(savedEin)}).`
              : strings.TAXES_EIN_ADD_PROMPT}
          </span>
          <button type="button" className={classes.einHintLink} onClick={handleSaveEin} disabled={savingEin}>
            {savedEin ? strings.TAXES_EIN_REPLACE_BUTTON : strings.TAXES_EIN_ADD_BUTTON}
          </button>
        </div>
      )}
    </form>
  )

  if (loading) {
    return (
      <Box className={classes.page}>
        <Loader />
      </Box>
    )
  }

  if (!records.length) {
    const startHelp = showEinError
      ? strings.TAXES_EIN_INVALID
      : (savedEin && ein === savedEin ? strings.TAXES_EIN_FROM_BUSINESS : strings.TAXES_EIN_NOT_ON_BUSINESS)

    return (
      <Box className={classes.page}>
        <Box className={classes.centeredState}>
          <Typography variant="h4" style={{ marginBottom: 12, fontWeight: 600 }}>
            {strings.TAXES_EMPTY_TITLE}
          </Typography>
          <Typography variant="body1" style={{ marginBottom: 28, color: MUTED, maxWidth: 520, lineHeight: 1.6 }}>
            {strings.TAXES_EMPTY_MESSAGE}
          </Typography>
          <form className={classes.startCard} onSubmit={handlePull}>
            <label htmlFor="taxes-ein" className={classes.startLabel}>{strings.TAXES_BUSINESS_EIN}</label>
            <input
              id="taxes-ein"
              className={classes.startInput}
              value={formatEin(ein)}
              onChange={(event) => setEin(cleanEin(event.target.value))}
              placeholder={strings.TAXES_EIN_PLACEHOLDER}
              inputMode="numeric"
              autoComplete="off"
              disabled={pulling || isReadOnlyUser}
            />
            <Typography className={classes.startHelp} style={showEinError ? { color: CRITICAL } : undefined}>
              {startHelp}
            </Typography>
            {demoChips}
            <button
              type="button"
          onClick={handlePull}
              className={`${classes.solidButton} ${classes.startButton}`}
              disabled={pullDisabled}
              style={pullDisabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
            >
              {pulling && <CircularProgress size={12} thickness={5} style={{ color: 'inherit' }} />}
              {pulling ? strings.TAXES_PULLING : strings.TAXES_RUN_REPORT}
              {!pulling && creditBadge}
            </button>
          </form>
        </Box>
      </Box>
    )
  }

  const status = snapshot?.currentStatus?.status || activeRecord.status
  const statusMeta = getStatusMeta(status)
  const pending = isPending(snapshot)
  const pulledAt = formatPulledAt(activeRecord.updatedAt || activeRecord.createdAt)
  const notes = buildTileNotes(snapshot)
  const headerPeriod = periods.length ? periods[0] : null
  const activeLiens = countActiveLiens(result)
  const activeAgreements = countActiveAgreements(result)

  const pageHead = (
    <div className={classes.pageHead}>
      <div>
        <div className={classes.titleRow}>
          <Typography className={classes.title}>{strings.TAXES_TITLE}</Typography>
          <StatusPill classes={classes} status={status} />
        </div>
        <Typography className={classes.subline}>
          {[
            strings.TAXES_PROVIDER,
            pulledAt ? `${strings.TAXES_PULLED_PREFIX} ${pulledAt}` : null
          ].filter(Boolean).join(' · ')}
        </Typography>
      </div>
      <div className={classes.headActions}>
        {einForm(classes.ghostButton, strings.TAXES_RE_PULL)}
      </div>
    </div>
  )

  const discardDialog = (
    <ConfirmationModal
      open={confirmingDiscard}
      onClose={() => { if (!discarding) setConfirmingDiscard(false) }}
      onSubmit={handleDiscard}
      isLoading={discarding}
      icon={ConfirmDeleteIcon}
      title={strings.TAXES_DISCARD_TITLE}
      bodyText={`${strings.TAXES_DISCARD_BODY} ${activeRecord.taxpayerName || formatEin(cleanEin(activeRecord.businessEin))}?`}
      subText={strings.TAXES_DISCARD_SUBTEXT}
      confirmText={strings.TAXES_DISCARD}
      color="secondary"
    />
  )

  const reportHead = (
    <div className={classes.reportHead}>
      <div>
        <Typography className={classes.eyebrow}>{headerMeta(result, periods, activeRecord)}</Typography>
        <Typography className={classes.name}>
          {result?.name || activeRecord.taxpayerName || `${strings.TAXES_EIN} ${activeRecord.businessEin}`}
        </Typography>
      </div>
      <div className={classes.reportHeadActions}>
        <StatusPill classes={classes} status={status} />
        {!isReadOnlyUser && (
          <button type="button" className={classes.discardButton} onClick={() => setConfirmingDiscard(true)}>
            <DeleteOutlineIcon style={{ fontSize: 16 }} />
            {strings.TAXES_DISCARD_REPORT}
          </button>
        )}
      </div>
    </div>
  )

  // No taxpayer matched the EIN (NotMonitored), or the stored snapshot is
  // unreadable - either way there are no figures to lay out.
  if (!result) {
    return (
      <div className={classes.page}>
        {pageHead}
        {historyRow}
        {discardDialog}
        <div className={classes.card}>
          {reportHead}
          <div className={classes.cardPad}>
            <div className={classes.verdict} style={{ backgroundColor: statusMeta.tint, marginBottom: 0 }}>
              <Typography className={classes.verdictHeadline}>
                {snapshot ? strings.TAXES_NOT_MONITORED_MESSAGE : strings.TAXES_SNAPSHOT_UNREADABLE}
              </Typography>
              {!!snapshot && (
                <Typography className={classes.verdictWhy}>
                  <span className={classes.verdictWhyLabel}>{strings.TAXES_WHY_THIS_MATTERS}</span>{' '}
                  {buildWhyThisMatters(snapshot)}
                </Typography>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.page}>
      {pageHead}
      {historyRow}
      {discardDialog}

      {/* Verdict and headline figures */}
      <div className={classes.card}>
        {reportHead}
        <div className={classes.cardPad}>
          <div className={classes.verdict} style={{ backgroundColor: statusMeta.tint }}>
            <Typography className={classes.verdictHeadline}>{buildHeadline(snapshot)}</Typography>
            <Typography className={classes.verdictWhy}>
              <span className={classes.verdictWhyLabel}>{strings.TAXES_WHY_THIS_MATTERS}</span>{' '}
              {buildWhyThisMatters(snapshot)}
            </Typography>
          </div>

          <div className={classes.tilesRow}>
            <div className={classes.tile}>
              <Typography className={classes.tileLabel}>{strings.TAXES_BALANCE_DUE}</Typography>
              <Typography className={classes.tileValue}>
                {pending ? strings.TAXES_PENDING_TILE : formatCurrency(result.amountDue)}
              </Typography>
              <Typography className={classes.tileNote}>{notes.balance}</Typography>
            </div>
            <div className={classes.tile}>
              <Typography className={classes.tileLabel}>{strings.TAXES_SECURED_BY_LIENS}</Typography>
              <Typography className={classes.tileValue}>
                {pending ? strings.TAXES_PENDING_TILE : formatCurrency(result.lienSummary?.totalBalanceSecuredByLiens)}
              </Typography>
              <Typography className={classes.tileNote}>{notes.liens}</Typography>
            </div>
            <div className={classes.tile}>
              <Typography className={classes.tileLabel}>{strings.TAXES_OUTSIDE_A_PAYMENT_PLAN}</Typography>
              <Typography className={classes.tileValue}>
                {pending
                  ? strings.TAXES_PENDING_TILE
                  : formatCurrency(result.installmentAgreementSummary?.balanceNotCoveredByActiveIA)}
              </Typography>
              <Typography className={classes.tileNote}>{notes.plan}</Typography>
            </div>
            <div className={classes.tile}>
              <Typography className={classes.tileLabel}>{strings.TAXES_MISSING_RETURNS}</Typography>
              <Typography className={classes.tileValue}>
                {pending ? strings.TAXES_PENDING_TILE : formatCount(result.numMissingReturns)}
              </Typography>
              <Typography className={classes.tileNote}>{notes.returns}</Typography>
            </div>
          </div>
        </div>
      </div>

      {/* Balance by year */}
      <div className={classes.card}>
        <div className={classes.cardHead}>
          <Typography className={classes.cardTitle}>{strings.TAXES_BALANCE_BY_YEAR}</Typography>
          {headerPeriod && (
            <Typography className={classes.cardMeta}>
              {strings.TAXES_FORM_LABEL} {headerPeriod.formName}, {strings.TAXES_PERIOD} {headerPeriod.taxPeriod}
            </Typography>
          )}
        </div>

        {years.length === 0 && (
          <div className={classes.emptyYears}>
            <Typography className={classes.emptyYearsTitle}>{strings.TAXES_NO_YEARS_TITLE}</Typography>
            <Typography className={classes.emptyYearsBody}>{strings.TAXES_NO_YEARS_BODY}</Typography>
          </div>
        )}

        {years.map((row, index) => {
          const expanded = openYear === row.year
          const owes = !!row.amountDue
          const period = findPeriodForYear(periods, row.year)
          const isLast = index === years.length - 1

          return (
            <React.Fragment key={row.year}>
              <div
                className={`${classes.yearRow} ${isLast && !expanded ? classes.yearRowLast : ''}`}
                onClick={() => setOpenYear(expanded ? null : row.year)}
              >
                <div className={classes.yearLeft}>
                  <Typography className={classes.yearLabel}>{row.year}</Typography>
                  <Typography className={classes.yearSubtitle}>{buildYearSubtitle(row)}</Typography>
                </div>
                <div className={classes.yearRight}>
                  <Typography className={classes.dueLabel}>{strings.TAXES_DUE}</Typography>
                  <Typography className={classes.dueValue} style={{ color: owes ? CRITICAL : INK }}>
                    {formatCurrency(row.amountDue)}
                  </Typography>
                  <button
                    type="button"
                    className={classes.toggle}
                    aria-label={String(row.year)}
                    aria-expanded={expanded}
                    onClick={(event) => { event.stopPropagation(); setOpenYear(expanded ? null : row.year) }}
                  >
                    {expanded ? '−' : '+'}
                  </button>
                </div>
              </div>

              {expanded && (
                <div className={`${classes.detailStrip} ${isLast ? classes.yearRowLast : ''}`}>
                  <div className={classes.detailCell}>
                    <Typography className={classes.detailLabel}>{strings.TAXES_PRINCIPAL}</Typography>
                    <Typography className={classes.detailValue}>{formatCurrency(row.principal)}</Typography>
                  </div>
                  <div className={classes.detailCell}>
                    <Typography className={classes.detailLabel}>{strings.TAXES_INTEREST}</Typography>
                    <Typography className={classes.detailValue}>{formatCurrency(row.interest)}</Typography>
                  </div>
                  <div className={classes.detailCell}>
                    <Typography className={classes.detailLabel}>{strings.TAXES_PENALTIES}</Typography>
                    <Typography className={classes.detailValue}>{formatCurrency(row.penalties)}</Typography>
                  </div>
                  <div className={classes.detailCell}>
                    <Typography className={classes.detailLabel}>{strings.TAXES_CREDITS}</Typography>
                    <Typography className={classes.detailValue}>{formatCurrency(row.allCredits)}</Typography>
                  </div>
                  {/* The payload carries no form or period on a balance year, so this
                      column appears only for a year that some lien or agreement names. */}
                  {period && (
                    <div className={classes.detailCell}>
                      <Typography className={classes.detailLabel}>{strings.TAXES_FORM_PERIOD}</Typography>
                      <Typography className={classes.detailValue}>{formatPeriodShort(period)}</Typography>
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>

      {/* Liens and agreements */}
      <div className={classes.cardsRow}>
        <DetailCard
          classes={classes}
          title={strings.TAXES_LIENS}
          count={activeLiens}
          pending={pending}
          tone={activeLiens ? getStatusMeta('NotCompliant') : null}
          summary={buildLienSummarySentence(result, pending)}
          showLabel={strings.TAXES_VIEW_FILING_DETAIL}
          hideLabel={strings.TAXES_HIDE_FILING_DETAIL}
          entries={pending ? [] : (result.liens || []).map((lien) => ({ key: lien.id, rows: buildLienDetailRows(lien) }))}
        />
        <DetailCard
          classes={classes}
          title={strings.TAXES_INSTALLMENT_AGREEMENTS}
          count={activeAgreements}
          pending={pending}
          tone={activeAgreements ? getStatusMeta('ActiveResolution') : null}
          summary={buildAgreementSummarySentence(result, pending)}
          showLabel={strings.TAXES_VIEW_AGREEMENT_DETAIL}
          hideLabel={strings.TAXES_HIDE_AGREEMENT_DETAIL}
          entries={pending ? [] : (result.installmentAgreements || []).map((agreement) => ({
            key: agreement.id,
            rows: buildAgreementDetailRows(agreement)
          }))}
        />
      </div>

    </div>
  )
}

const DetailCard = ({ classes, title, count, pending, tone, summary, entries, showLabel, hideLabel }) => {
  const [open, setOpen] = useState(false)

  return (
    <div className={`${classes.card} ${classes.halfCard}`}>
      <div className={classes.cardHead}>
        <Typography className={classes.cardTitle}>{title}</Typography>
        <span
          className={classes.countPill}
          style={tone
            ? { backgroundColor: tone.tint, color: tone.accent }
            : { backgroundColor: NEUTRAL_TINT, color: MUTED }}
        >
          {pending ? strings.TAXES_PENDING_PILL : `${count} ${strings.TAXES_ACTIVE_SUFFIX}`}
        </span>
      </div>
      <div className={classes.cardBody}>
        <Typography className={classes.bodyText}>{summary}</Typography>

        {entries.length > 0 && (
          <>
            <button type="button" className={classes.detailLink} onClick={() => setOpen(!open)}>
              {open ? hideLabel : showLabel}
            </button>
            {open && (
              <div className={classes.detailTable}>
                {entries.map((entry) => entry.rows.map((row) => (
                  <div className={classes.detailRow} key={`${entry.key}-${row.label}`}>
                    <span className={classes.detailRowLabel}>{row.label}</span>
                    <span className={classes.detailRowValue}>{row.value}</span>
                  </div>
                )))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

StatusPill.propTypes = {
  classes: PropTypes.object.isRequired,
  status: PropTypes.string,
  style: PropTypes.object
}

Taxes.propTypes = {
  applicationId: PropTypes.string,
  businessEin: PropTypes.string,
  creditCosts: PropTypes.array,
  onBusinessUpdated: PropTypes.func
}

DetailCard.propTypes = {
  classes: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  pending: PropTypes.bool,
  tone: PropTypes.object,
  summary: PropTypes.string.isRequired,
  entries: PropTypes.array.isRequired,
  showLabel: PropTypes.string.isRequired,
  hideLabel: PropTypes.string.isRequired
}

export default Taxes
